import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    service: '',
    garmentType: '',
    eventDate: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const response = await fetch('https://readdy.ai/api/form/d4faqlapf20hau00ntb0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData).toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          service: '',
          garmentType: '',
          eventDate: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    }

    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center bg-gradient-to-r from-amber-700 to-amber-900">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://public.readdy.ai/ai/img_res/c1f901c32bef12f5bab7574b414ed6b0.jpg"
            alt="Contact Us"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-6" style={{ fontFamily: '"Playfair Display", serif' }}>
            Schedule Your Home Visit
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto">
            Ready to transform your garments? Book a convenient home appointment and we'll come to you with everything needed for perfect alterations.
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6" style={{ fontFamily: '"Playfair Display", serif' }}>
                Book Your Home Appointment
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Fill out the form below and we'll contact you within 24 hours to schedule your convenient home visit.
              </p>

              <form onSubmit={handleSubmit} id="contact-form" data-readdy-form className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-700 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-700 focus:border-transparent text-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-700 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-700 focus:border-transparent text-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                      Service Needed
                    </label>
                    <div className="relative">
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-700 focus:border-transparent text-sm pr-8 appearance-none cursor-pointer"
                      >
                        <option value="">Select a service</option>
                        <option value="wedding-dress">Wedding Dress Alterations</option>
                        <option value="formal-wear">Formal Wear Alterations</option>
                        <option value="suit-tailoring">Suit Tailoring</option>
                        <option value="hem-adjustments">Hem Adjustments</option>
                        <option value="zipper-replacement">Zipper Replacement</option>
                        <option value="custom-fitting">Custom Fitting</option>
                        <option value="general-alterations">General Alterations</option>
                        <option value="other">Other</option>
                      </select>
                      <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="garmentType" className="block text-sm font-medium text-gray-700 mb-2">
                      Garment Type
                    </label>
                    <input
                      type="text"
                      id="garmentType"
                      name="garmentType"
                      value={formData.garmentType}
                      onChange={handleChange}
                      placeholder="e.g., Wedding dress, suit, pants"
                      className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-700 focus:border-transparent text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="eventDate" className="block text-sm font-medium text-gray-700 mb-2">
                    Event Date (if applicable)
                  </label>
                  <input
                    type="date"
                    id="eventDate"
                    name="eventDate"
                    value={formData.eventDate}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-700 focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Details & Preferred Address
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    maxLength={500}
                    placeholder="Please describe your alteration needs, preferred home visit address, timeline, or any specific requirements..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-700 focus:border-transparent text-sm resize-none"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-amber-700 text-white py-4 px-6 rounded-md font-semibold text-lg hover:bg-amber-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Sending...' : 'Request Home Visit'}
                </button>

                {submitStatus === 'success' && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-md">
                    <p className="text-green-800">
                      Thank you! Your message has been sent successfully. We'll get back to you within 24 hours.
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-red-800">
                      There was an error sending your message. Please try again or call us directly.
                    </p>
                  </div>
                )}
              </form>
            </div>

            {/* Contact Information */}
            <div>
              <h3 className="text-3xl font-bold text-gray-900 mb-6" style={{ fontFamily: '"Playfair Display", serif' }}>
                Service Area & Contact
              </h3>

              <div className="space-y-8">
                <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-lg shadow-md">
                  <h4 className="text-xl font-bold text-gray-900 mb-4">Contact Information</h4>
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <div className="w-12 h-12 flex items-center justify-center bg-amber-700 rounded-lg mr-4">
                        <i className="ri-map-pin-line text-xl text-white"></i>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">Service Area</p>
                        <p className="text-gray-600">New Jersey & surrounding areas including Jersey Shore, Long Branch, Brick, Newark, Jersey City</p>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <div className="w-12 h-12 flex items-center justify-center bg-amber-700 rounded-lg mr-4">
                        <i className="ri-phone-line text-xl text-white"></i>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">Phone</p>
                        <p className="text-gray-600">(908) 560-2447</p>
                      </div>
                    </div>

                    <div className="flex items-center">
                      <div className="w-12 h-12 flex items-center justify-center bg-amber-700 rounded-lg mr-4">
                        <i className="ri-mail-line text-xl text-white"></i>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">Email</p>
                        <p className="text-gray-600">style.stitchalterations25@gmail.com</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-lg shadow-md">
                  <h4 className="text-xl font-bold text-gray-900 mb-4">Home Visit Hours</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-700">Monday - Friday</span>
                      <span className="font-semibold text-gray-900">9:00 AM - 7:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Saturday</span>
                      <span className="font-semibold text-gray-900">10:00 AM - 6:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Sunday</span>
                      <span className="font-semibold text-gray-900">By Appointment</span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mt-4">
                    * Evening and weekend appointments available for your convenience
                  </p>
                </div>

                <div className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-lg shadow-md">
                  <h4 className="text-xl font-bold text-gray-900 mb-4">Prepare for Your Visit</h4>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <i className="ri-check-line text-amber-700 mr-2 mt-0.5"></i>
                      <span>Have your garments ready for fitting</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-amber-700 mr-2 mt-0.5"></i>
                      <span>Wear appropriate undergarments and shoes</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-amber-700 mr-2 mt-0.5"></i>
                      <span>Clear a small space for measurements</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-amber-700 mr-2 mt-0.5"></i>
                      <span>We bring all equipment and supplies</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h3 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: '"Playfair Display", serif' }}>
              Our Service Area
            </h3>
            <p className="text-xl text-gray-600">
              We serve New Jersey and surrounding areas including Jersey Shore, Long Branch, Brick, Newark, Jersey City
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1555476.8947!2d-74.7!3d40.2!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c0fb959e00409f%3A0x2cd27b07f83f6d8d!2sNew%20Jersey!5e0!3m2!1sen!2sus!4v1644000000000!5m2!1sen!2sus"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Stitch&Style Service Area"
            ></iframe>
          </div>

          <div className="mt-8 text-center">
            <p className="text-gray-600 mb-4">
              Serving Jersey Shore, Long Branch, Brick, Newark, Jersey City, and surrounding New Jersey communities
            </p>
            <p className="text-sm text-gray-500">
              Not sure if we serve your area? Contact us to check availability
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}